import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Cpu, Heart, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const AUTHORITY_BADGES = [
  "Reform Publishing LLC",
  "Reform Intelligence\u00AE",
  "VALOR, Inc. \u2014 501(c)(3)",
  "MENSA International Member",
  "U.S. Army Veteran",
  "Johns Hopkins",
  "Kennedy-Western University",
];

const PILLARS = [
  {
    icon: BookOpen,
    overline: "THE AUTHOR",
    title: "Books That Equip, Not Just Inspire",
    desc: "Financial literacy and entrepreneurship books written for people who are ready to act. Studying the Great Wealth Transfer since 2019.",
    link: "/books",
  },
  {
    icon: Cpu,
    overline: "THE TECHNOLOGIST",
    title: "AI Governance for the Missions That Matter",
    desc: "Technology and AI governance solutions for enterprise and DoD-adjacent clients navigating the policy and security implications of AI at scale.",
    link: "/reform-intelligence",
  },
  {
    icon: Heart,
    overline: "THE ADVOCATE",
    title: "No Veteran Should Face a Crisis Alone",
    desc: "VALOR and VALORLink\u2122 connect veterans in crisis with trained fellow veteran volunteers \u2014 synchronized with the VA Crisis Hotline.",
    link: "/valor",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.5 } }),
};

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [earlyEmail, setEarlyEmail] = useState("");
  const [earlyName, setEarlyName] = useState("");
  const [earlyStatus, setEarlyStatus] = useState("");
  const [subEmail, setSubEmail] = useState("");
  const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await api.get("/articles?published_only=true");
        setArticles(response.data.slice(0, 3));
      } catch (err) {
        console.error("Failed to fetch articles:", err.message);
      }
    };
    fetchArticles();
  }, []);

  const handleEarlyReader = async (e) => {
    e.preventDefault();
    if (!earlyEmail) return;
    try {
      await api.post("/subscribers", { email: earlyEmail, name: earlyName, list_type: "early_reader" });
      setEarlyStatus("You're on the list!");
      setEarlyEmail("");
      setEarlyName("");
      setTimeout(() => setEarlyStatus(""), 4000);
    } catch { setEarlyStatus("Error. Try again."); }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) return;
    try {
      await api.post("/subscribers", { email: subEmail, list_type: "general" });
      setSubStatus("Subscribed!");
      setSubEmail("");
      setTimeout(() => setSubStatus(""), 4000);
    } catch { setSubStatus("Error. Try again."); }
  };

  return (
    <div>
      <SEO
        title="Home"
        description="Dr. Ben Magee — Author, Serial Entrepreneur, U.S. Army Veteran, AI Governance Expert, and Nonprofit Founder. Studying the Great Wealth Transfer since 2019."
        keywords="Dr. Ben Magee, Great Wealth Transfer, minority entrepreneurship, financial literacy, AI governance, veteran crisis support, VALORLink"
        page="home"
        canonical="https://drbenmagee.com/"
      />
      {/* HERO */}
      <section data-testid="home-hero" className="relative bg-[#1B2A4A] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="w-1 h-16 bg-[#C9A84C] mb-8" />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1]"
              >
                The Transfer Is Happening.{" "}
                <span className="text-[#C9A84C]">The Question Is Whether You're Positioned for It.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mt-6 text-base md:text-lg text-white/70 tracking-wide"
              >
                Author &bull; Entrepreneur &bull; Veteran &bull; Advocate &bull; AI Governance Expert
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-10 flex flex-wrap gap-4"
              >
            <Link to="/books">
              <Button data-testid="hero-read-book-btn" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8 py-3 h-auto text-base">
                Read the Book
              </Button>
            </Link>
            <Link to="/insights">
              <Button data-testid="hero-read-insights-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-3 h-auto text-base">
                Read the Insights
              </Button>
            </Link>
          </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="hidden lg:flex justify-center"
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-[#C9A84C]/30 rounded-sm blur-sm" />
                <img
                  src="https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/d5hw9875_9D68558F-415B-4B81-BC77-5CFE62AE5B4C.png"
                  alt="Dr. Ben Magee"
                  data-testid="hero-headshot"
                  className="relative w-72 h-80 object-cover object-top rounded-sm border-2 border-[#C9A84C]/40"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AUTHORITY BAR */}
      <section data-testid="authority-bar" className="bg-[#E8EDF5] border-y border-[#1B2A4A]/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-5">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
            {AUTHORITY_BADGES.map((badge) => (
              <span key={badge} className="text-xs font-semibold tracking-wide text-[#1B2A4A]/60 whitespace-nowrap">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section data-testid="three-pillars" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">The Three Pillars</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1B2A4A] tracking-tight mb-12">
            Building at the Intersection of Knowledge, Technology & Service
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.overline}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                className="bg-[#E8EDF5] border border-[#1B2A4A]/10 rounded-sm overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="h-1 bg-[#1B2A4A]">
                  <div className="h-full w-1/3 bg-[#C9A84C]" />
                </div>
                <div className="p-8">
                  <p.icon className="h-8 w-8 text-[#C9A84C] mb-4" />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-2">{p.overline}</p>
                  <h3 className="font-heading font-semibold text-xl text-[#1B2A4A] mb-3">{p.title}</h3>
                  <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-6">{p.desc}</p>
                  <Link
                    to={p.link}
                    data-testid={`pillar-link-${p.overline.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C] hover:text-[#b59744] transition-colors"
                  >
                    Learn More <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section data-testid="featured-book" className="py-20 md:py-32 bg-[#F5EDD6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1B2A4A] text-white rounded-sm mb-4 hover:bg-[#1B2A4A]">Coming 2027 | Reform Publishing LLC</Badge>
              <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-[#1B2A4A] tracking-tight mb-4">
                THE GREAT WEALTH TRANSFER&trade;
              </h2>
              <p className="text-base md:text-lg text-[#1B2A4A]/70 italic mb-4">
                How Minority Entrepreneurs Can Claim Their Share of the Largest Wealth Shift in American History
              </p>
              <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-8">
                $124 trillion is changing hands. 6 million businesses are available for acquisition by 2035. Most minority entrepreneurs will miss it &mdash; not because the opportunity isn't there, but because no one handed them the blueprint. Until now.
              </p>
              <form onSubmit={handleEarlyReader} className="space-y-3 max-w-md">
                <Input
                  type="text"
                  placeholder="Your name"
                  value={earlyName}
                  onChange={(e) => setEarlyName(e.target.value)}
                  data-testid="early-reader-name"
                  className="rounded-sm border-[#1B2A4A]/20 focus:ring-[#C9A84C]"
                />
                <Input
                  type="email"
                  placeholder="Your email"
                  value={earlyEmail}
                  onChange={(e) => setEarlyEmail(e.target.value)}
                  data-testid="early-reader-email"
                  className="rounded-sm border-[#1B2A4A]/20 focus:ring-[#C9A84C]"
                />
                <Button
                  type="submit"
                  data-testid="early-reader-submit"
                  className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm"
                >
                  Join the Early Reader List
                </Button>
                {earlyStatus && <p className="text-sm text-[#1B2A4A] font-medium">{earlyStatus}</p>}
              </form>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_063e6bfb-ec62-4703-a8ad-86932ecf54db/artifacts/ulccn5tw_IMG_5784.jpeg" 
                alt="The Great Wealth Transfer by Dr. Ben Magee"
                className="h-[480px] object-contain rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* LATEST INSIGHTS */}
      <section data-testid="latest-insights" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-2">Latest Insights</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight">
                Original Analysis & Commentary
              </h2>
            </div>
            <Link to="/insights" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C]">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-[#E8EDF5] border border-[#1B2A4A]/10 rounded-sm overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="h-1 bg-[#C9A84C]" />
                <div className="p-6">
                  <Badge variant="secondary" className="text-xs mb-3 bg-[#1B2A4A]/10 text-[#1B2A4A] rounded-sm">
                    {a.category}
                  </Badge>
                  <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-2 line-clamp-2">{a.title}</h3>
                  <p className="text-sm text-[#1B2A4A]/60 line-clamp-2 mb-4">{a.excerpt}</p>
                  <Link
                    to={`/insights/${a.id}`}
                    data-testid={`article-card-${i}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C] hover:text-[#b59744]"
                  >
                    Read More <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          <Link to="/insights" className="md:hidden mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C]">
            View All Insights <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* EMAIL CAPTURE */}
      <section data-testid="email-capture" className="bg-[#1B2A4A] py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Insights Delivered to Your Inbox
          </h2>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Get original insights on the Great Wealth Transfer, minority entrepreneurship, and AI governance &mdash; delivered to your inbox. No noise. Just substance.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              data-testid="home-subscribe-email"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-sm flex-1 focus:ring-[#C9A84C]"
            />
            <Button
              type="submit"
              data-testid="home-subscribe-btn"
              className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm"
            >
              Subscribe
            </Button>
          </form>
          {subStatus && <p className="text-sm text-[#C9A84C] mt-3">{subStatus}</p>}
        </div>
      </section>
    </div>
  );
}
